if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}


class FlyPoints extends HTMLElement {
  constructor() {
    super();

    this.trigger = this.querySelector(".fly-points__trigger");
    this.modal = this.querySelector(".fly-points-modal");
    this.overlay = this.querySelector(".fly-points-modal__overlay");
    this.closeButton = this.querySelector(".fly-points-modal__close");
  }

  connectedCallback() {
    this.trigger.addEventListener("click", () => this.open());

    this.overlay.addEventListener("click", () => this.close());

    this.closeButton.addEventListener("click", () => this.close());

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        this.close();
      }
    });
  }

  open() {
    this.modal.hidden = false;

    requestAnimationFrame(() => {
      this.modal.classList.add("is-open");
    });

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.modal.classList.remove("is-open");

    setTimeout(() => {
      this.modal.hidden = true;
    }, 250);

    document.body.classList.remove("overflow-hidden");
  }
}

customElements.define("fly-points", FlyPoints);

class ProductPersonalizationFlyout extends HTMLElement {
  constructor() {
    super();
    this.activeTrigger = null;
    this.listening = false;
    this.onKeyDown = this.onKeyDown.bind(this);
  }

  refreshElements() {
    this.heading = this.querySelector('[data-dialog-heading]');
    this.closeButton = this.querySelector('.pp-close');
  }

  connectedCallback() {
    this.refreshElements();
    if (this.listening) return;
    this.addEventListener('keydown', this.onKeyDown);
    this.listening = true;
  }

  disconnectedCallback() {
    this.destroy();
  }

  open(trigger) {
    this.refreshElements();
    this.activeTrigger = trigger || document.activeElement;
    this.removeAttribute('inert');
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('active');
    document.body.classList.add('overflow-hidden');

    if (this.heading) {
      this.heading.focus();
    } else {
      this.getFocusableElements()[0]?.focus();
    }
  }

  close() {
    this.classList.remove('active');
    this.setAttribute('aria-hidden', 'true');
    this.setAttribute('inert', '');
    document.body.classList.remove('overflow-hidden');
    this.activeTrigger?.focus?.();
    this.activeTrigger = null;
  }

  destroy() {
    if (this.listening) {
      this.removeEventListener('keydown', this.onKeyDown);
      this.listening = false;
    }
    this.classList.remove('active');
    this.setAttribute('aria-hidden', 'true');
    this.setAttribute('inert', '');
    document.body.classList.remove('overflow-hidden');
    this.activeTrigger = null;
  }

  isFocusable(element) {
    const tabIndex = element.getAttribute('tabindex');
    if (
      element.matches(':disabled') ||
      element.getAttribute('aria-disabled') === 'true' ||
      (tabIndex !== null && Number(tabIndex) < 0)
    ) {
      return false;
    }

    let current = element;
    while (current && this.contains(current)) {
      if (
        current.hasAttribute('hidden') ||
        current.hasAttribute('inert') ||
        current.getAttribute('aria-hidden') === 'true' ||
        current.getAttribute('aria-disabled') === 'true'
      ) {
        return false;
      }

      const style = window.getComputedStyle(current);
      if (
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        style.visibility === 'collapse' ||
        style.contentVisibility === 'hidden'
      ) {
        return false;
      }
      current = current.parentElement;
    }

    return true;
  }

  getFocusableElements() {
    return [...this.querySelectorAll(
      'a[href], button, input, select, textarea, [tabindex]'
    )].filter((element) => this.isFocusable(element));
  }

  onKeyDown(event) {
    if (!this.classList.contains('active')) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      if (this.closeButton) {
        this.closeButton.click();
        if (this.classList.contains('active')) this.close();
      } else {
        this.close();
      }
      return;
    }

    if (event.key !== 'Tab') return;
    const focusable = this.getFocusableElements();
    if (focusable.length === 0) {
      event.preventDefault();
      this.heading?.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === this.heading)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    } else if (!focusable.includes(document.activeElement)) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('product-personalization-flyout')) {
  customElements.define(
    'product-personalization-flyout',
    ProductPersonalizationFlyout
  );
}

class ProductPersonalization extends HTMLElement {
  constructor() {
    super();
    this.eventController = null;
    this.state = {
      confirmed: false,
      initials: '',
      font: '',
      colour: '',
      variantId: null
    };
  }

  connectedCallback() {
    if (this.eventController) return;
    this.cache();
    if (!this.flyout) return;
    this.loadVariantMap();
    this.eventController = new AbortController();
    this.bindEvents();
    this.setDefaultOptions();
    this.render();
  }

  disconnectedCallback() {
    this.eventController?.abort();
    this.eventController = null;
    this.closeAllDropdowns();
  }

  cache() {
    this.flyout = document.getElementById(this.dataset.flyoutId);
    if (!this.flyout) return;

    this.previews = [
      ...this.querySelectorAll('.pp-preview__text'),
      ...this.flyout.querySelectorAll('.pp-preview__text')
    ];
    this.input = this.flyout.querySelector('.pp-input');
    this.summary = this.querySelector('.pp-card__summary');
    this.addButton = this.querySelector('.pp-add');
    this.confirmButton = this.flyout.querySelector('.pp-confirm');
    this.cancelButton = this.flyout.querySelector('.pp-cancel');
    this.removeButton = this.flyout.querySelector('.pp-remove');
    this.closeButton = this.flyout.querySelector('.pp-close');
    this.overlay = this.flyout.querySelector('.pp-flyout__overlay');
    this.fontDropdown = this.flyout.querySelector('[data-type="font"]');
    this.colourDropdown = this.flyout.querySelector('[data-type="colour"]');
  }

  loadVariantMap() {
    const json = this.querySelector('.pp-variant-map');
    this.variantMap = json ? JSON.parse(json.textContent) : {};
  }

  listen(target, type, callback) {
    target?.addEventListener(type, callback, {
      signal: this.eventController.signal
    });
  }

  bindEvents() {
    this.listen(this.addButton, 'click', (event) => this.openFlyout(event.currentTarget));
    this.querySelectorAll('.pp-edit').forEach((element) => {
      this.listen(element, 'click', (event) => this.openFlyout(event.currentTarget));
    });
    this.listen(this.confirmButton, 'click', () => this.confirm());
    this.listen(this.cancelButton, 'click', () => this.cancel());
    this.listen(this.removeButton, 'click', () => this.reset());
    this.listen(this.closeButton, 'click', () => this.cancel());
    this.listen(this.overlay, 'click', () => this.cancel());
    this.listen(this.input, 'input', (event) => this.handleInput(event));
    this.bindDropdown(this.fontDropdown);
    this.bindDropdown(this.colourDropdown);
    this.listen(document, 'click', (event) => {
      if (!event.target.closest('.pp-listbox')) this.closeAllDropdowns();
    });
  }

  handleInput(event) {
    const value = event.target.value
      .replace(/[^a-zA-Z0-9]/g, '')
      .substring(0, 5)
      .toUpperCase();
    event.target.value = value;
    this.state.initials = value;
    this.renderPreview();
  }

  setDefaultOptions() {
    this.setDefaultSelections();
    this.renderPreview();
  }

  setDefaultSelections() {
    const firstFont = this.fontDropdown.querySelector('.pp-option');
    if (firstFont) {
      this.state.font = firstFont.dataset.value;
      const value = this.fontDropdown.querySelector('.pp-listbox__value');
      value.textContent = firstFont.dataset.value;
      value.style.fontFamily = `var(--${firstFont.dataset.value.toLowerCase()})`;
      this.updateSelectedOption(this.fontDropdown, firstFont);
    }

    const firstColour = this.colourDropdown.querySelector('.pp-option');
    if (firstColour) {
      this.state.colour = firstColour.dataset.value;
      const value = this.colourDropdown.querySelector('.pp-listbox__value');
      const swatch = value.querySelector('.pp-option__swatch');
      const label = value.querySelector('.pp-option__label');
      value.classList.add('has-value');
      swatch.style.background = firstColour.dataset.colour;
      label.textContent = firstColour.dataset.value;
      this.updateSelectedOption(this.colourDropdown, firstColour);
    }

    this.updateVariant();
  }

  closeDropdown(dropdown) {
    dropdown?.classList.remove('is-open');
    dropdown?.querySelector('.pp-listbox__trigger')?.setAttribute('aria-expanded', 'false');
  }

  closeAllDropdowns() {
    this.flyout?.querySelectorAll('.pp-listbox').forEach((dropdown) => {
      this.closeDropdown(dropdown);
    });
  }

  openDropdown(dropdown, option) {
    this.closeAllDropdowns();
    dropdown.classList.add('is-open');
    dropdown.querySelector('.pp-listbox__trigger').setAttribute('aria-expanded', 'true');
    option?.focus();
  }

  bindDropdown(dropdown) {
    const trigger = dropdown.querySelector('.pp-listbox__trigger');
    const options = [...dropdown.querySelectorAll('.pp-option')];

    this.listen(trigger, 'click', (event) => {
      event.stopPropagation();
      const isOpen = dropdown.classList.contains('is-open');
      if (isOpen) {
        this.closeDropdown(dropdown);
      } else {
        const selected = options.find((option) => option.getAttribute('aria-selected') === 'true');
        this.openDropdown(dropdown, selected || options[0]);
      }
    });

    this.listen(trigger, 'keydown', (event) => {
      if (event.key === 'Escape' && dropdown.classList.contains('is-open')) {
        event.preventDefault();
        event.stopPropagation();
        this.closeDropdown(dropdown);
        return;
      }
      const targetIndex = event.key === 'ArrowUp' || event.key === 'End'
        ? options.length - 1
        : event.key === 'ArrowDown' || event.key === 'Home'
          ? 0
          : -1;
      if (targetIndex !== -1) {
        event.preventDefault();
        this.openDropdown(dropdown, options[targetIndex]);
      }
    });

    options.forEach((option, index) => {
      this.listen(option, 'click', () => this.selectDropdownOption(dropdown, option));
      this.listen(option, 'keydown', (event) => {
        if (event.key === 'Tab') {
          event.preventDefault();
          event.stopPropagation();
          this.moveFocusOutsideDropdown(dropdown, event.shiftKey);
          return;
        }
        let targetIndex = index;
        if (event.key === 'ArrowDown') targetIndex = Math.min(index + 1, options.length - 1);
        else if (event.key === 'ArrowUp') targetIndex = Math.max(index - 1, 0);
        else if (event.key === 'Home') targetIndex = 0;
        else if (event.key === 'End') targetIndex = options.length - 1;
        else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.selectDropdownOption(dropdown, option);
          trigger.focus();
          return;
        } else if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          this.closeDropdown(dropdown);
          trigger.focus();
          return;
        } else {
          return;
        }
        event.preventDefault();
        options[targetIndex].focus();
      });
    });
  }

  moveFocusOutsideDropdown(dropdown, backwards) {
    const trigger = dropdown.querySelector('.pp-listbox__trigger');
    const focusable = this.flyout.getFocusableElements();
    const triggerIndex = focusable.indexOf(trigger);
    const targetIndex = triggerIndex + (backwards ? -1 : 1);
    const target = focusable[targetIndex];

    this.closeDropdown(dropdown);
    (target || trigger).focus();
  }

  updateSelectedOption(dropdown, selectedOption) {
    dropdown.querySelectorAll('.pp-option').forEach((option) => {
      option.setAttribute('aria-selected', option === selectedOption ? 'true' : 'false');
    });
  }

  selectDropdownOption(dropdown, option) {
    const value = dropdown.querySelector('.pp-listbox__value');
    if (dropdown.dataset.type === 'font') {
      this.state.font = option.dataset.value;
      value.textContent = option.dataset.value;
      value.style.fontFamily = `var(--${this.state.font.toLowerCase()})`;
    } else {
      this.state.colour = option.dataset.value;
      const swatch = value.querySelector('.pp-option__swatch');
      const label = value.querySelector('.pp-option__label');
      value.classList.add('has-value');
      swatch.style.background = option.dataset.colour;
      label.textContent = option.dataset.value;
    }
    this.updateSelectedOption(dropdown, option);
    this.updateVariant();
    this.renderPreview();
    this.closeDropdown(dropdown);
  }

  renderPreview() {
    this.previews.forEach((preview) => {
      preview.textContent = this.state.initials;
      preview.style.fontFamily = this.state.font
        ? `var(--${this.state.font.toLowerCase()})`
        : '';
      preview.style.color = this.state.colour
        ? `var(--${this.state.colour.toLowerCase()})`
        : '';
    });
  }

  updateVariant() {
    const key = `${this.state.font} / ${this.state.colour}`;
    this.state.variantId = this.variantMap[key]?.id || null;
  }

  confirm() {
    if (!this.state.initials || !this.state.font || !this.state.colour) return;
    this.state.confirmed = true;
    this.updateVariant();
    this.dataset.variantId = this.state.variantId;
    this.dataset.initials = this.state.initials;
    this.dataset.font = this.state.font;
    this.dataset.colour = this.state.colour;
    this.render();
    this.closeFlyout();
  }

  cancel() {
    if (!this.state.confirmed) this.reset();
    this.closeFlyout();
  }

  reset() {
    this.state = {
      confirmed: false,
      initials: '',
      font: '',
      colour: '',
      variantId: null
    };
    this.input.value = '';
    delete this.dataset.variantId;
    delete this.dataset.initials;
    delete this.dataset.font;
    delete this.dataset.colour;
    this.setDefaultSelections();
    this.render();
  }

  render() {
    this.dataset.state = this.state.confirmed ? 'confirmed' : 'empty';
    this.renderPreview();
    this.summary.textContent = this.state.confirmed
      ? `${this.state.initials} · ${this.state.font} · ${this.state.colour}`
      : '';
  }

  getItem() {
    if (!this.state.confirmed) return null;
    return {
      id: this.state.variantId,
      quantity: 1,
      properties: {
        Initials: this.state.initials,
        Font: this.state.font,
        Colour: this.state.colour
      }
    };
  }

  openFlyout(trigger) {
    this.flyout.open(trigger);
  }

  closeFlyout() {
    this.closeAllDropdowns();
    this.flyout.close();
  }
}

if (!customElements.get('product-personalization')) {
  customElements.define(
    'product-personalization',
    ProductPersonalization
  );
}


class StickyAddToCart extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector('.sticky-atc__button');
    this.productInfo = document.querySelector('.product__info-container');
    this.mainCTA = document.querySelector('.product-form__submit');

    if (!this.productInfo || !this.mainCTA) return;

    this.button?.addEventListener('click', this.onClick.bind(this));

    this.createObserver();
  }

  createObserver() {
    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.classList.toggle('is-visible', !entry.isIntersecting);
      },
      {
        threshold: 0
      }
    );

    this.observer.observe(this.mainCTA);
  }

  onClick(event) {
    event.preventDefault();

    const headerHeight =
      document.querySelector('.section-header')?.offsetHeight ||
      document.querySelector('.header')?.offsetHeight ||
      0;

    const top =
      this.productInfo.getBoundingClientRect().top +
      window.scrollY -
      headerHeight -
      16;

    window.scrollTo({
      top,
      behavior: 'smooth'
    });

    this.productInfo.classList.add('is-highlight');

    setTimeout(() => {
      this.productInfo.classList.remove('is-highlight');
    }, 1000);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

customElements.define('sticky-add-to-cart', StickyAddToCart);
